import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppLoadingRow } from '../components/AppBootLoading'
import { pathForAppState } from '../navigation/appPaths'
import { TrainingDocumentIcon } from './TrainingDocumentIcon'
import {
  formatTrainingListDateFromDoc,
  getTrainingListPreviewText,
  normalizeTrainingFeature,
  sortTrainingDocuments,
  trainingDocumentKey,
  type TrainingFeatureTarget,
  type TrainingListSortMode,
} from './trainingDocument'
import {
  TRAINING_DETAIL_SCREEN_BY_LIST,
  TRAINING_SCENARIOS_SCREEN,
  TRAINING_TOOLBOX_TIPS_SCREEN,
} from './trainingScreens'
import { useTrainingDocuments } from './useTrainingDocuments'

const SORT_KEYS: TrainingListSortMode[] = ['newest', 'oldest', 'category']

const SORT_LABEL: Record<TrainingListSortMode, string> = {
  newest: 'Newest',
  oldest: 'Oldest',
  category: 'Category',
}

type TrainingDocumentsListScreenProps = {
  featureTarget: TrainingFeatureTarget
  listScreenId: typeof TRAINING_SCENARIOS_SCREEN | typeof TRAINING_TOOLBOX_TIPS_SCREEN
}

export function TrainingDocumentsListScreen({
  featureTarget,
  listScreenId,
}: TrainingDocumentsListScreenProps) {
  const { documents, loading, error } = useTrainingDocuments()
  const [sortMode, setSortMode] = useState<TrainingListSortMode>('newest')
  const detailScreenId = TRAINING_DETAIL_SCREEN_BY_LIST[listScreenId] ?? listScreenId

  const filteredDocs = useMemo(
    () => documents.filter((d) => normalizeTrainingFeature(d) === featureTarget),
    [documents, featureTarget],
  )
  const sortedDocs = useMemo(
    () => sortTrainingDocuments(filteredDocs, sortMode),
    [filteredDocs, sortMode],
  )

  return (
    <section className="app-download-card training-docs-card">
      <div className="training-sort-bar">
        <span className="training-sort-bar-label">Sort by:</span>
        {SORT_KEYS.map((key) => {
          const active = sortMode === key
          return (
            <button
              key={key}
              type="button"
              className={active ? 'training-sort-chip training-sort-chip-active' : 'training-sort-chip'}
              onClick={() => setSortMode(key)}
            >
              {SORT_LABEL[key]}
            </button>
          )
        })}
      </div>
      {loading ? (
        <AppLoadingRow label="Loading…" />
      ) : error ? (
        <p className="app-download-error">{error}</p>
      ) : !filteredDocs.length ? (
        <p className="app-download-empty">No items available yet. Check back soon.</p>
      ) : (
        <ul className="training-doc-list">
          {sortedDocs.map((doc) => {
            const teaser = getTrainingListPreviewText(doc)
            const cat = doc.categoryName?.trim()
            const dateStr = formatTrainingListDateFromDoc(doc)
            const to = pathForAppState(null, [detailScreenId], undefined, {
              trainingDocId: trainingDocumentKey(doc),
            })
            return (
              <li key={trainingDocumentKey(doc)}>
                <Link to={to} className="training-doc-card">
                  <TrainingDocumentIcon document={doc} size="list" />
                  <span className="training-doc-card-text">
                    <span className="training-doc-card-title">{doc.title}</span>
                    {cat || dateStr ? (
                      <span className="training-doc-card-meta">
                        {cat ? <span>{cat}</span> : null}
                        {dateStr ? <span>{dateStr}</span> : null}
                      </span>
                    ) : null}
                    {teaser ? <span className="training-doc-card-teaser">{teaser}</span> : null}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
